import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, ProgressBar, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import businessService from '../services/businessService';
import BackToDashboard from '../components/BackToDashboard';

function AnalyzeBusiness() {
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [salesPageUrl, setSalesPageUrl] = useState('');

  // Results state
  const [extractedData, setExtractedData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [hasPreviousData, setHasPreviousData] = useState(false);
  const [previousData, setPreviousData] = useState(null);

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    const result = await authService.checkAuth();
    if (result.authenticated) {
      setUser(result.user);
      // Check if they already have analyzed data
      await loadPreviousData();
    } else {
      navigate('/login');
    }
    setLoading(false);
  };

  const loadPreviousData = async () => {
    // Load all scraped fields
    const fields = [
      'business_name', 'industry', 'core_product', 'core_product_description',
      'features_benefits', 'benefits_list', 'target_audience', 'value_proposition',
      'competitive_advantages', 'transformation_promise', 'pain_points_addressed',
      'brand_voice_hint', 'mission_hint', 'vision_hint', 'business_scope',
      'customer_type', 'primary_location', 'website_url', 'sales_page_url'
    ];
    const data = {};
    let hasData = false;

    for (const field of fields) {
      const result = await businessService.getField(field);
      if (result.success && result.data.field_value) {
        data[field] = result.data.field_value;
        hasData = true;
      }
    }

    if (hasData && data.business_name) {
      setHasPreviousData(true);
      setPreviousData(data);
    }
  };

  // Helper to format field names for display
  const formatFieldName = (field) => {
    const labels = {
      business_name: 'Business Name',
      industry: 'Industry',
      core_product: 'Core Product',
      core_product_description: 'Product Description',
      features_benefits: 'Features & Benefits',
      benefits_list: 'Key Benefits',
      target_audience: 'Target Audience',
      value_proposition: 'Value Proposition (USP)',
      competitive_advantages: 'Competitive Advantages',
      transformation_promise: 'Transformation Promise',
      pain_points_addressed: 'Pain Points Addressed',
      brand_voice_hint: 'Brand Voice',
      mission_hint: 'Mission (Draft)',
      vision_hint: 'Vision (Draft)',
      business_scope: 'Business Scope',
      customer_type: 'Customer Type',
      primary_location: 'Primary Location',
      website_url: 'Website',
      sales_page_url: 'Sales Page'
    };
    return labels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Helper to format field values (handle arrays, long text)
  const formatFieldValue = (value) => {
    if (!value) return null;

    // Parse JSON arrays
    if (typeof value === 'string' && value.startsWith('[')) {
      try {
        const arr = JSON.parse(value);
        if (Array.isArray(arr)) {
          return arr.map((item, i) => <li key={i}>{item}</li>);
        }
      } catch (e) {
        // Not valid JSON, treat as string
      }
    }

    // Handle bullet points already in text
    if (typeof value === 'string' && value.includes('\n- ')) {
      return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>;
    }

    return value;
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setError('');

    if (!websiteUrl.trim()) {
      setError('Please enter your website URL');
      return;
    }

    setAnalyzing(true);

    try {
      const response = await fetch('/api/scrape/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          website_url: websiteUrl.trim(),
          sales_page_url: salesPageUrl.trim() || null
        })
      });

      const data = await response.json();

      if (data.success) {
        setExtractedData(data.data);
        setShowConfirmation(true);
      } else {
        setError(data.error || 'Failed to analyze your website. Please check the URL and try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Analyze error:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleConfirmAndContinue = async () => {
    // Save extracted data to business profile
    setAnalyzing(true);

    try {
      const response = await fetch('/api/scrape/save-extracted', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          extracted_data: extractedData
        })
      });

      const data = await response.json();

      if (data.success) {
        // Mark welcome as completed and proceed to dashboard
        await businessService.saveField('welcome_completed', 'true');
        navigate('/dashboard');
      } else {
        setError(data.error || 'Failed to save data. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Save error:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSkip = async () => {
    // Skip analysis and go directly to dashboard
    await businessService.saveField('welcome_completed', 'true');
    navigate('/dashboard');
  };

  const handleStartOver = () => {
    setExtractedData(null);
    setShowConfirmation(false);
    setError('');
  };

  // Edit field handlers
  const startEditing = (section, key, currentValue) => {
    setEditingField({ section, key });
    setEditValue(Array.isArray(currentValue) ? currentValue.join(', ') : (currentValue || ''));
  };

  const saveEdit = () => {
    if (!editingField) return;

    const { section, key } = editingField;
    const newData = { ...extractedData };

    // Parse value back to array if it was an array
    let valueToSave = editValue;
    if (newData[section] && Array.isArray(newData[section][key])) {
      valueToSave = editValue.split(',').map(s => s.trim()).filter(s => s);
    }

    // Update the nested data
    if (newData[section]) {
      newData[section] = { ...newData[section], [key]: valueToSave };
    }

    // Also update summary if editing a summary field
    if (section === 'foundation_data' || section === 'business_model_data') {
      const summaryKey = key;
      if (newData.summary && summaryKey in newData.summary) {
        newData.summary = { ...newData.summary, [summaryKey]: valueToSave };
      }
    }

    setExtractedData(newData);
    setEditingField(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  // Render an editable field
  const renderEditableField = (section, key, value, isLongText = false) => {
    const isEditing = editingField?.section === section && editingField?.key === key;
    let displayValue = value;
    if (Array.isArray(value)) {
      displayValue = value.length > 0 ? value.join(', ') : null;
    }

    if (isEditing) {
      return (
        <div className="d-flex align-items-start gap-2">
          {isLongText ? (
            <Form.Control
              as="textarea"
              rows={3}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              autoFocus
              className="flex-grow-1"
            />
          ) : (
            <Form.Control
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              autoFocus
              className="flex-grow-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
            />
          )}
          <Button size="sm" variant="success" onClick={saveEdit}>Save</Button>
          <Button size="sm" variant="outline-secondary" onClick={cancelEdit}>Cancel</Button>
        </div>
      );
    }

    return (
      <div
        onClick={() => startEditing(section, key, value)}
        style={{ cursor: 'pointer' }}
        className="editable-field p-1 rounded"
        title="Click to edit"
      >
        {displayValue || <span className="text-muted fst-italic">Click to add</span>}
        <small className="text-primary ms-2" style={{ fontSize: '0.7em' }}>(edit)</small>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  // Show previous data if exists and not currently analyzing new data
  if (hasPreviousData && !showConfirmation && !analyzing) {
    // Group fields for display
    const fieldGroups = {
      'Business Overview': ['business_name', 'industry', 'business_scope', 'customer_type', 'primary_location'],
      'Product & Value': ['core_product', 'core_product_description', 'value_proposition', 'transformation_promise'],
      'Benefits & Features': ['features_benefits', 'benefits_list', 'competitive_advantages', 'pain_points_addressed'],
      'Target & Brand': ['target_audience', 'brand_voice_hint'],
      'Mission & Vision (Drafts)': ['mission_hint', 'vision_hint'],
      'Source URLs': ['website_url', 'sales_page_url']
    };

    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card className="shadow-lg">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <h2 className="mb-2">What We Found on Your Website</h2>
                  <p className="text-muted">
                    This data has been saved to pre-populate your Foundation and Business Model.
                  </p>
                </div>

                {Object.entries(fieldGroups).map(([groupName, fields]) => {
                  // Only show group if it has at least one field with data
                  const hasGroupData = fields.some(f => previousData[f]);
                  if (!hasGroupData) return null;

                  return (
                    <Card className="mb-3" key={groupName}>
                      <Card.Header className="bg-light">
                        <strong>{groupName}</strong>
                      </Card.Header>
                      <Card.Body>
                        {fields.map(field => {
                          if (!previousData[field]) return null;
                          const formattedValue = formatFieldValue(previousData[field]);
                          const isList = Array.isArray(formattedValue);

                          return (
                            <div key={field} className="mb-3">
                              <strong className="text-primary">{formatFieldName(field)}:</strong>
                              {isList ? (
                                <ul className="mb-0 mt-1">{formattedValue}</ul>
                              ) : (
                                <p className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>{formattedValue}</p>
                              )}
                            </div>
                          );
                        })}
                      </Card.Body>
                    </Card>
                  );
                })}

                <div className="d-flex justify-content-between mt-4">
                  <Button variant="outline-secondary" onClick={() => navigate('/dashboard')}>
                    Back to Dashboard
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      setHasPreviousData(false);
                      setPreviousData(null);
                    }}
                  >
                    Re-Analyze Website
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Confirmation screen after analysis
  if (showConfirmation && extractedData) {
    const quality = extractedData.quality || {};
    const summary = extractedData.summary || {};
    const foundationData = extractedData.foundation_data || {};
    const businessModelData = extractedData.business_model_data || {};

    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card className="shadow-lg">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <h2 className="mb-2">Here is What We Found</h2>
                  <p className="text-muted">
                    Review the information below. This will pre-populate your Foundation and Business Model screens.
                  </p>
                </div>

                {/* Quality indicator */}
                <Alert variant={quality.overall_completeness >= 70 ? 'success' : quality.overall_completeness >= 40 ? 'warning' : 'info'} className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Data Quality Score</strong>
                    <Badge bg={quality.overall_completeness >= 70 ? 'success' : quality.overall_completeness >= 40 ? 'warning' : 'secondary'}>
                      {quality.overall_completeness || 0}%
                    </Badge>
                  </div>
                  <ProgressBar
                    now={quality.overall_completeness || 0}
                    variant={quality.overall_completeness >= 70 ? 'success' : quality.overall_completeness >= 40 ? 'warning' : 'info'}
                    className="mb-2"
                  />
                  <small>
                    {quality.overall_completeness >= 70
                      ? 'Great! We found plenty of information to get you started.'
                      : quality.overall_completeness >= 40
                      ? 'We found some information. You will want to fill in the gaps as you go through the modules.'
                      : 'Limited information found. The guided journey will help you fill in the details.'}
                  </small>
                </Alert>

                {/* Quick Summary */}
                <Card className="mb-4 border-primary" style={{ borderWidth: '2px' }}>
                  <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <strong>Quick Summary</strong>
                    <small>(click any field to edit)</small>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <p className="mb-2"><strong>Business:</strong> {renderEditableField('summary', 'business_name', summary.business_name)}</p>
                        <p className="mb-2"><strong>Industry:</strong> {renderEditableField('summary', 'industry', summary.industry)}</p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2"><strong>Type:</strong> {renderEditableField('summary', 'business_type', summary.business_type)}</p>
                        <p className="mb-2"><strong>Target Market:</strong> {renderEditableField('summary', 'target_market', summary.target_market)}</p>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Foundation Data */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>Foundation Module Data</strong>
                      <Badge bg="secondary">{quality.foundation_fields_found || 0}/{quality.foundation_fields_total || 6} fields</Badge>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {Object.entries(foundationData).map(([key, value]) => {
                        const isLongField = ['target_market_description'].includes(key);
                        return (
                          <Col md={6} key={key} className="mb-3">
                            <small className="text-muted text-capitalize d-block mb-1">{key.replace(/_/g, ' ')}:</small>
                            {renderEditableField('foundation_data', key, value, isLongField)}
                          </Col>
                        );
                      })}
                    </Row>
                  </Card.Body>
                </Card>

                {/* Business Model Data */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>Business Model Data</strong>
                      <Badge bg="secondary">{quality.business_model_fields_found || 0}/{quality.business_model_fields_total || 9} fields</Badge>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {Object.entries(businessModelData).map(([key, value]) => {
                        const isLongField = ['core_product_description', 'value_proposition', 'transformation_promise', 'pain_points_addressed'].includes(key);
                        return (
                          <Col md={6} key={key} className="mb-3">
                            <small className="text-muted text-capitalize d-block mb-1">{key.replace(/_/g, ' ')}:</small>
                            {renderEditableField('business_model_data', key, value, isLongField)}
                          </Col>
                        );
                      })}
                    </Row>
                  </Card.Body>
                </Card>

                {/* Needs deeper conversation warning */}
                {quality.needs_deeper_conversation && (
                  <Alert variant="info" className="mb-4">
                    <strong>Note:</strong> We found limited information on your website. The guided journey
                    will include additional questions to help build a complete picture of your business.
                  </Alert>
                )}

                {/* Action buttons */}
                <div className="d-flex justify-content-between">
                  <Button variant="outline-secondary" onClick={handleStartOver}>
                    Try Different URLs
                  </Button>
                  <div>
                    <Button variant="outline-primary" onClick={handleSkip} className="me-2">
                      Skip Pre-fill
                    </Button>
                    <Button
                      variant="success"
                      size="lg"
                      onClick={handleConfirmAndContinue}
                      disabled={analyzing}
                    >
                      {analyzing ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Looks Good - Continue'
                      )}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Initial URL input screen
  return (
    <Container className="py-5">
      <BackToDashboard module="foundation" />
      <Row className="justify-content-center">
        <Col md={8} lg={7}>
          <Card className="shadow-lg">
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <h1 className="display-5 mb-3">Let Us Learn About Your Business</h1>
                <p className="lead text-muted">
                  Enter your website URL and we will automatically extract key information
                  to pre-populate your profile. This saves you time!
                </p>
              </div>

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleAnalyze}>
                <Form.Group className="mb-4">
                  <Form.Label><strong>Your Website URL</strong> <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="https://yourwebsite.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    disabled={analyzing}
                    size="lg"
                  />
                  <Form.Text className="text-muted">
                    Your main business website or landing page
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label><strong>Sales Page URL</strong> <span className="text-muted">(optional)</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="https://yourwebsite.com/sales-page"
                    value={salesPageUrl}
                    onChange={(e) => setSalesPageUrl(e.target.value)}
                    disabled={analyzing}
                    size="lg"
                  />
                  <Form.Text className="text-muted">
                    If you have a specific sales or product page, add it here for richer data
                  </Form.Text>
                </Form.Group>

                <div className="d-grid gap-2 mt-4">
                  <Button
                    variant="primary"
                    size="lg"
                    type="submit"
                    disabled={analyzing || !websiteUrl.trim()}
                  >
                    {analyzing ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Analyzing Your Website...
                      </>
                    ) : (
                      'Analyze My Website'
                    )}
                  </Button>
                </div>
              </Form>

              <hr className="my-4" />

              <div className="text-center">
                <p className="text-muted small mb-2">
                  Do not have a website yet? No problem!
                </p>
                <Button variant="link" onClick={handleSkip}>
                  Skip this step and enter information manually
                </Button>
              </div>

              <Alert variant="light" className="mt-4 small">
                <strong>What we extract:</strong> Business name, industry, target market, products/services,
                pricing, value proposition, and more. You can review and edit everything before it is saved.
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default AnalyzeBusiness;
